import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import NewsLayout from "../../components/news/NewsLayout";
import { Skeleton } from "../../components/ui/skeleton";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";
import Seo, { breadcrumbLd, collectionPageLd } from "../../components/news/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const NewsCategory = () => {
  const { category } = useParams();
  const { publicProject, publicProjectLoading } = useProject();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publicProjectLoading) return;
    const fetchArticles = async () => {
      setLoading(true);
      try {
        if (publicProject) {
          const res = await axios.get(`${API}/public/articles?project_id=${publicProject.id}&category=${category}&limit=30`);
          setArticles(res.data);
        }
      } catch (e) {
        console.error("Failed to fetch:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [category, publicProject, publicProjectLoading]);

  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || "News";
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const categoryLabel = category ? category.charAt(0).toUpperCase() + category.slice(1) : "";
  const heroArticle = articles?.[0];

  const jsonLd = [
    breadcrumbLd([
      { name: "Home", url: siteUrl + "/" },
      { name: categoryLabel, url: pageUrl },
    ]),
    collectionPageLd({
      name: `${categoryLabel} news`,
      description: `Latest ${category} coverage from ${siteName} — updated in real time.`,
      url: pageUrl,
      siteName,
      siteUrl,
      items: (articles || []).slice(0, 30).map((a) => ({
        url: `${siteUrl}/article/${a.slug}`,
        name: a.title,
      })),
    }),
  ];

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <Seo
        title={`${categoryLabel} news`}
        description={`Latest ${category} news from ${siteName} — real-time updates, in-depth analysis, and top stories from around the world.`}
        canonical={pageUrl}
        siteName={siteName}
        image={heroArticle?.featured_image || heroArticle?.image_url}
        imageAlt={heroArticle ? `${heroArticle.title} — top ${category} story` : `${categoryLabel} news from ${siteName}`}
        imageWidth={1200}
        imageHeight={630}
        jsonLd={jsonLd}
      />
      {theme === "espresso" && <EspressoCategory category={category} articles={articles} loading={loading} />}
      {theme === "rio" && <RioCategory category={category} articles={articles} loading={loading} />}
      {theme !== "espresso" && theme !== "rio" && <BusinessCategory category={category} articles={articles} loading={loading} />}
    </NewsLayout>
  );
};

const EspressoCategory = ({ category, articles, loading }) => (
  <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
    <p className="font-meta mb-3" style={{ color: "var(--em-accent)" }}>Section</p>
    <h1 className="font-display text-5xl md:text-6xl font-bold capitalize mb-4" style={{ color: "var(--em-text)" }}>{category}</h1>
    <p className="text-lg mb-12 max-w-2xl" style={{ color: "var(--em-text-muted)" }}>Considered reporting on {category}, served with your morning coffee.</p>
    {loading ? <GridSkeleton theme="espresso" /> : articles.length === 0 ? (
      <p style={{ color: "var(--em-text-muted)" }}>No stories in this section yet.</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {articles.map((a) => (
          <Link key={a.id} to={`/article/${a.slug}`} className="block group" data-testid={`category-article-${a.id}`}>
            <div className="em-image-wrap aspect-[5/4] mb-5 overflow-hidden">
              {a.featured_image ? <img src={a.featured_image} alt={a.image_alt || `${a.title} — ${a.category} story`} width="600" height="480" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: "var(--em-surface)" }} />}
            </div>
            <p className="font-meta mb-2" style={{ color: "var(--em-accent)" }}>{a.category}</p>
            <h3 className="font-display text-xl font-bold leading-snug mb-2 group-hover:opacity-80 transition-opacity" style={{ color: "var(--em-text)" }}>{a.title}</h3>
            <p className="font-meta" style={{ color: "var(--em-text-muted)" }}>{formatDate(a.created_at)}</p>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const RioCategory = ({ category, articles, loading }) => (
  <div className="max-w-7xl mx-auto px-4 lg:px-6 py-10">
    <div className="flex items-baseline justify-between mb-8 pb-3" style={{ borderBottom: "2px solid var(--rio-accent)" }}>
      <h1 className="font-display uppercase text-5xl md:text-6xl" style={{ color: "var(--rio-text)", fontFamily: "'Anton', sans-serif" }}>{category}</h1>
      <span className="font-mono text-xs uppercase" style={{ color: "var(--rio-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{articles.length} STORIES</span>
    </div>
    {loading ? <GridSkeleton theme="rio" /> : articles.length === 0 ? (
      <p className="font-mono uppercase" style={{ color: "var(--rio-text-muted)" }}>// NO STORIES IN FEED</p>
    ) : (
      <div className="grid grid-cols-12 gap-0" style={{ border: "1px solid var(--rio-border)" }}>
        {articles.map((a) => (
          <Link key={a.id} to={`/article/${a.slug}`} className="col-span-12 sm:col-span-6 lg:col-span-3 block group" style={{ borderRight: "1px solid var(--rio-border)", borderBottom: "1px solid var(--rio-border)" }} data-testid={`category-article-${a.id}`}>
            <div className="aspect-[4/3] overflow-hidden">
              {a.featured_image ? <img src={a.featured_image} alt={a.image_alt || `${a.title} — ${a.category} wire`} width="600" height="450" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: "var(--rio-surface)" }} />}
            </div>
            <div className="p-4">
              <p className="font-mono text-[0.65rem] uppercase mb-2" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>{a.category}</p>
              <h3 className="font-display text-base uppercase leading-tight" style={{ color: "var(--rio-text)" }}>{a.title}</h3>
            </div>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const BusinessCategory = ({ category, articles, loading }) => (
  <div className="max-w-7xl mx-auto px-4 py-8">
    <h1 className="text-4xl font-bold capitalize mb-8">{category}</h1>
    {loading ? <GridSkeleton theme="business" /> : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {articles.map((a) => (
          <Link key={a.id} to={`/article/${a.slug}`} className="block">
            <div className="aspect-[4/3] bg-slate-200 rounded-lg overflow-hidden mb-3">
              {a.featured_image && <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover" />}
            </div>
            <h3 className="font-semibold">{a.title}</h3>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const GridSkeleton = ({ theme }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-72 w-full" style={theme === "rio" ? { borderRadius: 0 } : undefined} />)}
  </div>
);

export default NewsCategory;
